'use client';
import React, { useMemo } from 'react';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface StrengthLevel {
  score: number;
  label: string;
  color: string;
  bgColor: string;
}

function getStrength(password: string): StrengthLevel {
  if (!password) return { score: 0, label: '', color: 'transparent', bgColor: 'var(--border)' };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: 'Weak', color: '#EF4444', bgColor: 'rgba(239,68,68,0.15)' };
  if (score === 2) return { score: 2, label: 'Fair', color: '#F59E0B', bgColor: 'rgba(245,158,11,0.15)' };
  if (score === 3) return { score: 3, label: 'Good', color: '#3B82F6', bgColor: 'rgba(59,130,246,0.15)' };
  if (score === 4) return { score: 4, label: 'Strong', color: '#10B981', bgColor: 'rgba(16,185,129,0.15)' };
  return { score: 5, label: 'Very Strong', color: '#10B981', bgColor: 'rgba(16,185,129,0.15)' };
}

const CRITERIA = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'Uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Number', test: (p: string) => /[0-9]/.test(p) },
  { label: 'Special character', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export default function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const strength = useMemo(() => getStrength(password), [password]);

  if (!password) return null;

  const filledBars = strength.score;
  const totalBars = 4;

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bars */}
      <div className="flex items-center gap-2">
        <div className="flex gap-1 flex-1">
          {Array.from({ length: totalBars }).map((_, i) => (
            <div
              key={i}
              className="h-1.5 flex-1 rounded-full transition-all duration-300"
              style={{
                background: i < filledBars ? strength.color : 'var(--border)',
                opacity: i < filledBars ? 1 : 0.4,
              }}
            />
          ))}
        </div>
        <span
          className="text-[10px] font-semibold min-w-[60px] text-right transition-all duration-300"
          style={{ color: strength.color }}
        >
          {strength.label}
        </span>
      </div>

      {/* Criteria checklist */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {CRITERIA.map((criterion) => {
          const met = criterion.test(password);
          return (
            <div key={criterion.label} className="flex items-center gap-1.5">
              <div
                className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200"
                style={{
                  background: met ? 'rgba(16,185,129,0.15)' : 'var(--border)',
                  border: `1px solid ${met ? '#10B981' : 'transparent'}`,
                }}
              >
                {met && (
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path d="M1.5 4L3 5.5L6.5 2" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span
                className="text-[10px] transition-colors duration-200"
                style={{ color: met ? 'var(--foreground)' : 'var(--muted-foreground)' }}
              >
                {criterion.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
